<script lang="ts">
    // Svelte
    import { createEventDispatcher, onMount, onDestroy } from 'svelte';
    import type { SvelteComponent } from 'svelte';

    // Stores
    import { getI18n } from '~/stores/i18n';

    // Shared Components
    import Modal from '@amp/web-app-components/src/components/Modal/Modal.svelte';
    import Button from '@amp/web-app-components/src/components/buttons/Button.svelte';

    export let headerText: string;
    export let submitText: string;
    export let isOpen: boolean;
    export let title: string = '';
    export let description: string = '';
    export let isPublic: boolean = false;

    const dispatch = createEventDispatcher();
    const i18n = getI18n();

    $: editableTitle = title || '';
    $: editableDescription = description || '';
    $: editableIsPublic = isPublic;

    let modalElement: SvelteComponent;

    function handleSubmit() {
        dispatch('submit', {
            title: editableTitle,
            description: editableDescription,
            isPublic: editableIsPublic,
        });
    }

    function close() {
        dispatch('close');
    }

    $: {
        if (isOpen) {
            modalElement?.showModal();
        } else {
            modalElement?.close();
            editableTitle = editableDescription = '';
            editableIsPublic = false;
        }
    }

    onMount(() => {
        window.addEventListener('popstate', close);
    });

    onDestroy(() => {
        window.removeEventListener('popstate', close);
    });
</script>

<Modal modalTriggerElement={null} bind:this={modalElement} on:close={close}>
    <form
        class="playlist-form"
        data-testid="playlist-form"
        on:submit|preventDefault={handleSubmit}
    >
        <h3 class="modal-title">{headerText}</h3>
        <input
            class="playlist-title"
            name="title"
            type="text"
            data-testid="playlist-title-input"
            bind:value={editableTitle}
            aria-label={$i18n.t(
                'ASE.Web.MusicClassical.Library.PlaylistCreation.TitleLabel',
            )}
            placeholder={$i18n.t(
                'ASE.Web.MusicClassical.Library.PlaylistCreation.TitlePlaceholder',
            )}
            required={true}
        />

        <textarea
            class="description"
            name="description"
            data-testid="playlist-description-input"
            aria-label={$i18n.t(
                'ASE.Web.MusicClassical.Library.PlaylistCreation.DescriptionLabel',
            )}
            bind:value={editableDescription}
            placeholder={$i18n.t(
                'ASE.Web.MusicClassical.Library.PlaylistCreation.DescriptionPlaceholder',
            )}
            required={false}
            rows="2"
        />

        <div class="buttons">
            <div class="cancel-button">
                <Button
                    buttonStyle="buttonB"
                    type="button"
                    on:buttonClick={close}
                >
                    {$i18n.t(
                        'ASE.Web.MusicClassical.Library.PlaylistCreation.Cancel',
                    )}
                </Button>
            </div>
            <div class="submit-button">
                <Button
                    buttonStyle="buttonA"
                    type="submit"
                    disabled={editableTitle.trim().length < 1}
                >
                    {submitText}
                </Button>
            </div>
        </div>
    </form>
</Modal>

<style lang="scss">
    @use '@amp/web-shared-styles/app/core/globalvars' as *;

    .playlist-form {
        width: 310px;
        max-height: 100vh; // Ensure modal is still usable on short viewport
        overflow-y: auto;
        display: flex;
        flex-flow: column;
        background: var(--pageBG);
    }

    .modal-title {
        margin: 12px;
        word-break: break-word;
        text-align: center;
        font: var(--title-3-bold);
    }

    .playlist-title,
    .description {
        margin: 0 20px;

        // UXD TODO: placeholder styles borrowed from search, waiting on Design spec
        border: 1px solid var(--searchBarBorderColor);
        border-radius: $global-border-radius-xsmall;
        padding: 5px;
        resize: none;
        font: var(--body);
        font-family: inherit;

        &::placeholder {
            color: var(--systemSecondary);
        }
    }

    .playlist-title {
        margin-top: 26px;
    }

    .description {
        margin-top: 10px;
        margin-bottom: 10px;
    }

    .public {
        margin: 17px 20px 21px;
        display: flex; // Makes it easier to align checkbox and text
        gap: 4px;
        accent-color: var(--keyColor); // Accent color is inherited by checkbox
        word-break: break-word;
    }

    .buttons {
        height: 52px;
        padding: 12px;
        display: flex;
        justify-content: space-between;
        position: sticky;
        bottom: 0;
        background: var(--pageBG);

        // UXD TODO: Placeholder styles, waiting on Design spec
        // stylelint-disable order/order
        --buttonWidth: auto;
        --buttonMinWidth: 100px;
        --buttonHeight: 28px;
        // stylelint-enable order/order
        border-top: var(--keyline-border-style);
        z-index: var(--z-default);
    }

    .cancel-button {
        // UXD TODO: Placeholder styles, waiting on Design spec
        --buttonTextColor: var(--systemPrimary);
        --buttonBorderColor: var(--searchBarBorderColor);
    }
</style>
